import axios from 'axios'
import request from './request'
import { useUserStore } from '@/stores/user'
import { WEB_CLIENT_TYPE, getWebDeviceId } from '@/utils/auth-session'
import { resolveDownloadFileName } from '@/utils/downloadFileName'

/**
 * 流程模板管理 API
 */

// blob 导出专用实例：复用 baseURL 与认证/设备头，但不走全局响应拦截器
// （全局拦截器对 blob 响应直接 return response.data，会丢弃 content-disposition）
const blobRequest = axios.create({
  baseURL: request.defaults.baseURL,
  timeout: 60000
})
blobRequest.interceptors.request.use((config) => {
  const userStore = useUserStore()
  if (userStore.accessToken) {
    config.headers.Authorization = `${userStore.tokenType} ${userStore.accessToken}`
  }
  config.headers['X-Device-Id'] = getWebDeviceId()
  config.headers['X-Client-Type'] = WEB_CLIENT_TYPE
  return config
})

// 分页查询流程模板
export function getTemplatePage(params) {
  return request({
    url: '/workflow/templates/page',
    method: 'post',
    data: params
  })
}

// 获取流程模板详情
export function getTemplateDetail(id) {
  return request({
    url: `/workflow/templates/${id}`,
    method: 'get'
  })
}

// 创建流程模板
export function createTemplate(data) {
  return request({
    url: '/workflow/templates',
    method: 'post',
    data
  })
}

// 更新流程模板
export function updateTemplate(data) {
  return request({
    url: '/workflow/templates',
    method: 'put',
    data
  })
}

// 删除流程模板
export function deleteTemplate(data) {
  return request({
    url: '/workflow/templates',
    method: 'delete',
    data
  })
}

// 更新流程模板状态
export function updateTemplateStatus(data) {
  return request({
    url: '/workflow/templates/status',
    method: 'put',
    data
  })
}

// 复制流程模板
export function copyTemplate(id) {
  return request({
    url: `/workflow/templates/${id}/copy`,
    method: 'post'
  })
}

// 导出流程模板（BPMN XML）
export function exportTemplateBpmn(id) {
  return request({
    url: `/workflow/templates/${id}/export/bpmn`,
    method: 'get',
    responseType: 'blob'
  })
}

// 导入流程模板
export function importTemplate(data) {
  return request({
    url: '/workflow/templates/import',
    method: 'post',
    data,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 流程分类 API
 */

// 获取流程分类列表
export function getWorkflowCategories(params) {
  return request({
    url: '/workflow/categories',
    method: 'get',
    params
  })
}

// 创建流程分类
export function createWorkflowCategory(data) {
  return request({
    url: '/workflow/categories',
    method: 'post',
    data
  })
}

// 更新流程分类
export function updateWorkflowCategory(data) {
  return request({
    url: '/workflow/categories',
    method: 'put',
    data
  })
}

// 删除流程分类
export function deleteWorkflowCategory(params) {
  return request({
    url: '/workflow/categories',
    method: 'delete',
    params
  })
}

// 获取流程分类及模板列表（用于发起审批选择）
export function getCategoriesWithTemplates() {
  return request({
    url: '/workflow/categories/with-templates',
    method: 'get'
  })
}

// 获取审批路径（包含审批人）
export function getApprovalPath(templateId, formData) {
  return request({
    url: `/workflow/templates/${templateId}/approval-path`,
    method: 'get',
    params: formData ? { formData: JSON.stringify(formData) } : undefined
  })
}

// 预览审批路径（支持发起时选择审批人/抄送人）
export function previewApprovalPath(templateId, data) {
  return request({
    url: `/workflow/templates/${templateId}/approval-path/preview`,
    method: 'post',
    data
  })
}

/**
 * 审批实例 API
 */

// 发起审批
export function startApproval(data) {
  return request({
    url: '/workflow/start',
    method: 'post',
    data
  })
}

/**
 * 待办任务 API
 */

// 获取我的待办任务列表
export function getMyTasks(params) {
  return request({
    url: '/workflow/tasks/my',
    method: 'post',
    data: params
  })
}

// 获取抄送给我的记录
export function getMyCcRecords(params) {
  return request({
    url: '/workflow/cc/my',
    method: 'post',
    data: params
  })
}

// 标记抄送记录已读
export function markCcRead(ccRecordId) {
  return request({
    url: `/workflow/cc/${ccRecordId}/read`,
    method: 'put'
  })
}

// 一键将我的未读抄送全部标记为已读
export function markAllCcRead() {
  return request({
    url: '/workflow/cc/read-all',
    method: 'put'
  })
}

/**
 * 审批实例 API
 */

// 获取我发起的审批列表
export function getMyInstances(params) {
  return request({
    url: '/workflow/instances/my',
    method: 'post',
    data: params
  })
}

// 审批操作（通过/驳回）
export function approveTask(data) {
  return request({
    url: '/workflow/approve',
    method: 'post',
    data
  })
}

// 取消审批（仅申请人可取消自己发起的审批）
export function cancelApproval(data) {
  return request({
    url: '/workflow/cancel',
    method: 'post',
    data
  })
}

// 获取流程实例详情
export function getInstanceDetail(instanceId) {
  return request({
    url: `/workflow/instances/${instanceId}/detail`,
    method: 'get'
  })
}

/**
 * 导出审批单据 Word（docx）
 * 走独立 blobRequest 实例以保留响应头（文件名在 content-disposition 的 filename* 中）
 * @param {string|number} instanceId 审批实例 ID
 * @returns {Promise<{ blob: Blob, fileName: string }>} 文件二进制内容与解析后的文件名
 */
export async function exportApprovalDocx(instanceId) {
  const response = await blobRequest({
    url: `/workflow/instances/${instanceId}/export`,
    method: 'get',
    responseType: 'blob'
  })
  return {
    blob: response.data,
    fileName: resolveDownloadFileName(response.headers?.['content-disposition'], `审批单据-${instanceId}.docx`)
  }
}

/**
 * 导出流程统计（双 sheet xlsx：清单 + 汇总）
 * 走独立 blobRequest 实例以保留响应头（文件名在 content-disposition 的 filename* 中）
 * @param {Object} params 导出参数
 * @param {string} params.templateCode 流程模板编码
 * @param {string} params.startDate 开始日期（yyyy-MM-dd）
 * @param {string} params.endDate 结束日期（yyyy-MM-dd）
 * @returns {Promise<{ blob: Blob, fileName: string }>} 文件二进制内容与解析后的文件名
 */
export async function exportWorkflowStats(params) {
  const response = await blobRequest({
    url: '/workflow/stats/export',
    method: 'post',
    data: params,
    responseType: 'blob'
  })
  return {
    blob: response.data,
    fileName: resolveDownloadFileName(response.headers?.['content-disposition'], `${params.templateCode}统计.xlsx`)
  }
}
